/**
 * MobileNotificationBell + Notification Center (mobile-first bottom sheet)
 *
 * Reuses the canonical in-app notification infrastructure:
 *   GET  /api/notifications/in-app
 *   GET  /api/notifications/in-app/count
 *   POST /api/notifications/in-app/{id}/read
 *   POST /api/notifications/in-app/read-all
 *
 * Bell shows canonical unread count. Tapping opens a bottom-sheet center that
 * lists notifications newest-first, grouped IDAG / IGÅR / TIDIGARE. Rows mark
 * as read on tap and deep-link via the stored notification.url (falling back
 * to type-based routing for legacy chat notifications).
 */

import React, { useState } from 'react';
import useNotificationInbox from '../../hooks/useNotificationInbox';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../lib/config';
import {
  Bell, CheckCheck, MessageSquare, CalendarDays, ClipboardList,
  Shirt, Trophy, Users, ChevronRight, Trash2,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import { cn } from '../../lib/utils';

const API_URL = API_BASE;

const getMeta = (type) => {
  switch (type) {
    case 'chat_message':
    case 'chat_dm':
    case 'chat_coach':
    case 'chat_leader':
    case 'chat_team':
    case 'chat_training':
    case 'chat_match':
      return { icon: MessageSquare, color: 'text-sky-400', bg: 'bg-sky-400/10' };
    case 'squad_selection':
      return { icon: Shirt, color: 'text-emerald-400', bg: 'bg-emerald-400/10' };
    case 'evaluation_request':
    case 'evaluation':
      return { icon: ClipboardList, color: 'text-amber-400', bg: 'bg-amber-400/10' };
    case 'event_new':
    case 'event_reminder':
    case 'attendance_reminder':
      return { icon: CalendarDays, color: 'text-violet-400', bg: 'bg-violet-400/10' };
    case 'praise_received':
    case 'new_praise':
      return { icon: Trophy, color: 'text-yellow-400', bg: 'bg-yellow-400/10' };
    case 'team_update':
      return { icon: Users, color: 'text-blue-400', bg: 'bg-blue-400/10' };
    default:
      return { icon: Bell, color: 'text-[var(--t-text-secondary)]', bg: "bg-[rgb(var(--tn-tint-ffffff))]/[0.05]" };
  }
};

const relativeTime = (dateString) => {
  const date = new Date(dateString);
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 1) return 'Nyss';
  if (mins < 60) return `${mins} min sedan`;
  if (hours < 24) return `${hours} tim sedan`;
  if (days < 7) return `${days} d sedan`;
  return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
};

// Group buckets: IDAG / IGÅR / TIDIGARE (Stockholm-ish, local time).
const bucketFor = (dateString) => {
  const d = new Date(dateString);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startYesterday = startToday - 86400000;
  const t = d.getTime();
  if (t >= startToday) return 'IDAG';
  if (t >= startYesterday) return 'IGÅR';
  return 'TIDIGARE';
};

export default function MobileNotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const {
    notifications, unreadCount, readCount, unreadOnly, setUnreadOnly,
    loading, error, busy, refresh, hasMore, loadMore,
    markRead, markAllRead, remove, clearRead,
  } = useNotificationInbox(open);

  const destinationFor = (n) => {
    const storedUrl = n.url && n.url !== '/' ? n.url : n.data?.url;
    if (storedUrl?.startsWith('/') && !storedUrl.startsWith('//')) return storedUrl;
    const { type, data } = n;
    if (type?.startsWith('chat')) return data?.channel_id ? `/chat?channel=${data.channel_id}` : '/chat';
    if (type?.startsWith('event') || type === 'attendance_reminder') {
      return data?.event_id ? `/calendar?event=${data.event_id}` : '/calendar';
    }
    if (type?.startsWith('evaluation')) return '/evaluations';
    if (type === 'praise_received' || type === 'new_praise') return '/praise';
    return null;
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    const dest = destinationFor(n);
    setOpen(false);
    if (dest) navigate(dest);
  };

  // Order + group (list already comes newest-first from the API).
  const groups = [];
  const byBucket = {};
  notifications.forEach((n) => {
    const b = bucketFor(n.created_at);
    if (!byBucket[b]) { byBucket[b] = []; groups.push(b); }
    byBucket[b].push(n);
  });

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative flex items-center justify-center h-11 w-11 rounded-full text-[var(--t-text-primary)] active:bg-[rgb(var(--tn-tint-ffffff))]/[0.06] transition-colors shrink-0"
        aria-label="Notiser"
        data-testid="mobile-notification-bell"
      >
        <Bell className="h-[19px] w-[19px]" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--primary)] text-[rgb(var(--tn-text-ffffff))] text-[10px] font-bold flex items-center justify-center leading-none"
            data-testid="mobile-notification-badge"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="bg-[var(--t-bg1)] border-t border-[var(--border)] rounded-t-2xl p-0 max-h-[85vh] flex flex-col"
          data-testid="mobile-notification-center"
        >
          <SheetHeader className="px-4 pt-4 pb-3 flex-row items-center justify-between space-y-0 border-b border-[rgb(var(--tn-border-ffffff))]/[0.05]">
            <SheetTitle className="text-[var(--t-text-primary)] text-base">Notiser</SheetTitle>
            {unreadCount > 0 && (
              <button
                disabled={busy}
                onClick={markAllRead}
                className="flex items-center gap-1.5 text-xs font-medium text-[var(--primary)] active:opacity-70"
                data-testid="mobile-notification-mark-all"
              >
                <CheckCheck className="h-4 w-4" />
                Markera alla lästa
              </button>
            )}
          </SheetHeader>

          <div className="flex items-center gap-2 px-4 py-1 border-b border-[rgb(var(--tn-border-ffffff))]/[0.05] shrink-0">
            <button className={`min-h-11 px-3 text-xs rounded-full ${!unreadOnly ? 'text-primary bg-primary/10' : 'text-muted-foreground'}`} aria-pressed={!unreadOnly} onClick={() => setUnreadOnly(false)}>Alla</button>
            <button className={`min-h-11 px-3 text-xs rounded-full ${unreadOnly ? 'text-primary bg-primary/10' : 'text-muted-foreground'}`} aria-pressed={unreadOnly} onClick={() => setUnreadOnly(true)}>Olästa</button>
            <button disabled={busy || readCount === 0} onClick={clearRead} className="ml-auto min-h-11 px-2 text-xs text-muted-foreground disabled:opacity-40">Rensa lästa</button>
          </div>
          {error && <button onClick={refresh} className="p-3 text-xs text-[rgb(var(--tn-text-f87171))]">Kunde inte hämta notiser. Försök igen.</button>}
          <div className="overflow-y-auto min-h-0 flex-1 pb-[env(safe-area-inset-bottom)]">
            {loading ? <p className="p-6 text-center text-sm text-muted-foreground">Hämtar notiser…</p> : notifications.length === 0 ? (
              <div className="py-16 text-center text-[var(--t-text-secondary)]">
                <Bell className="h-10 w-10 mx-auto mb-3 opacity-25" />
                <p className="text-sm">{unreadOnly ? "Inga olästa notiser" : "Inga notiser"}</p>
              </div>
            ) : (
              groups.map((bucket) => (
                <div key={bucket}>
                  <div className="px-4 pt-4 pb-1.5 text-[11px] font-semibold tracking-wider text-[var(--t-text-secondary)]/60">
                    {bucket}
                  </div>
                  {byBucket[bucket].map((n) => {
                    const meta = getMeta(n.type);
                    const Icon = meta.icon;
                    const dest = destinationFor(n);
                    return (
                      <div key={n.id} className="flex items-center border-b border-[rgb(var(--tn-border-ffffff))]/[0.03]">
                      <button
                        onClick={() => handleClick(n)}
                        className={cn(
                          'flex-1 min-w-0 flex items-start gap-3 pl-4 pr-1 py-3 text-left transition-colors',
                          !n.read ? "bg-[rgb(var(--tn-tint-ffffff))]/[0.035] active:bg-[rgb(var(--tn-tint-ffffff))]/[0.06]" : "active:bg-[rgb(var(--tn-tint-ffffff))]/[0.03]"
                        )}
                        data-testid={`mobile-notification-item-${n.id}`}
                      >
                        <div className={cn('w-9 h-9 rounded-full flex items-center justify-center shrink-0', meta.bg)}>
                          <Icon className={cn('w-[18px] h-[18px]', meta.color)} strokeWidth={1.9} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={cn(
                              'text-sm truncate',
                              !n.read ? 'font-semibold text-[var(--t-text-primary)]' : 'font-medium text-[var(--t-text-secondary)]'
                            )}>
                              {n.title}
                            </p>
                            {!n.read && <span className="w-2 h-2 rounded-full bg-[var(--primary)] shrink-0" />}
                          </div>
                          <p className="text-xs text-[var(--t-text-secondary)] line-clamp-2 mt-0.5">{n.body}</p>
                          <p className="text-[10px] text-[var(--t-text-secondary)]/60 mt-1">{relativeTime(n.created_at)}</p>
                        </div>
                        {dest && <ChevronRight className="w-4 h-4 text-[var(--t-text-secondary)]/40 shrink-0 mt-2" />}
                      </button>
                      <button type="button" disabled={busy} onClick={() => remove(n.id)} aria-label="Ta bort notis" className="h-11 w-11 mr-1 shrink-0 flex items-center justify-center text-muted-foreground active:text-[rgb(var(--tn-text-f87171))] disabled:opacity-40">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
            {hasMore && <button disabled={loading} onClick={loadMore} className="w-full min-h-11 text-sm text-primary">Visa fler</button>}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
